import ShoppingCart from "@dana/MobiusOverrides/Icons/ShoppingCart";

import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import loadEmergencyCart from "@dana/Store/Data/EmergencyCart/EmergencyCartHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationStateDana) => {
    const shouldLoadCart = (!getCurrentCartState(state).value && !getCurrentCartState(state).isLoading);
    const totalCountDisplay = getCurrentCartState(state).value?.totalCountDisplay;
    const orderTotal = getCurrentCartState(state).value?.orderSubTotal;
    const orderTotalDisplay = getCurrentCartState(state).value?.orderSubTotalDisplay;
    const cartUrl = getPageLinkByPageType(state, "CartPage")?.url;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const emergencyState = (state as any).emergencyCartState;
    const cartState = getCurrentCartState(state);
    const page = getCurrentPage(state);

    const isOnePageCheckoutPage = page.type === "OnePageCheckoutPage";
    return {
        shouldLoadCart,
        totalCountDisplay,
        orderTotal,
        orderTotalDisplay,
        cartUrl,
        isAuthenticated,
        isCartLoading: cartState.isLoading,
        emergencyCart: emergencyState.cart,
        isOnePageCheckoutPage
    };
}

const mapDispatchToProps = {
    loadCurrentCart,
    loadEmergencyCart,
};

export interface CartLinkStyles {
    cartWrapper: InjectableCss;
    routerLink?: LinkPresentationProps;
    desktopCartPrice?: TypographyPresentationProps;
    cartCountWrapper: InjectableCss;
    desktopCartCount?: TypographyPresentationProps;
    authorizedCartCount?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    cartWrapper: {},
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        css: css`
            height: 50px;
            padding: 0;
        `,
    },
    desktopCartPrice: {},
    cartCountWrapper: {},
    desktopCartCount: {},
    compactCountText: {},
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    orderTotalDisplay,
    orderTotal,
    isAuthenticated,
    emergencyCart,
    loadEmergencyCart,
    isCartLoading,
    isOnePageCheckoutPage
}) => {

    useEffect(() => {
        loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        loadEmergencyCart({ cartId: API_URL_CURRENT_FRAGMENT }); // initial load only
    }, []);

    const prevIsLoadingRef = React.useRef<boolean>(false);

    useEffect(() => {
        if (prevIsLoadingRef.current === true && isCartLoading === false) {
            loadEmergencyCart({ cartId: API_URL_CURRENT_FRAGMENT });
        }
        prevIsLoadingRef.current = isCartLoading;
    }, [isCartLoading]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    const retainedTotalCountToDisplay = React.useMemo(() => {
        if (!isAuthenticated) { return 0; }
        const emergencyCount = Number(emergencyCart?.totalCountDisplay) || 0;
        const standardCount = Number(totalCountDisplay) || 0;
        if (isOnePageCheckoutPage) {
            return standardCount;
        }
        return emergencyCount + standardCount;
    }, [isAuthenticated, emergencyCart?.totalCountDisplay, totalCountDisplay,isOnePageCheckoutPage]);

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <>
                {orderTotal && orderTotal > 0 && (
                    <Typography as="span" {...styles.compactCountText}>
                        {orderTotalDisplay}
                    </Typography>
                )}

                <Link
                    {...routerLink}
                    color={theme.header.linkColor}
                    href={cartUrl}
                    icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                    data-test-selector="cartLink"
                >
                    <Typography as="span" {...styles.compactCountText}>
                        {retainedTotalCountToDisplay}
                    </Typography>
                    <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
                </Link>
            </>
        );
    }

    return (
        <>
            {isAuthenticated ? (
                <Link {...routerLink} href={cartUrl} data-test-selector="cartLink">
                    <StyledWrapper {...styles.cartWrapper} className="cartWrapper">
                        {orderTotal && orderTotal > 0 ? (
                            <Typography as="span" {...styles.desktopCartPrice} data-test-selector="cartLinkTotal">
                                {orderTotalDisplay}
                            </Typography>
                        ) : null}
                        <Hidden as="span" className="desktop-cart-count-wrapper" {...styles.cartCountWrapper}>
                            <Icon src={ShoppingCart} size={18} />
                            {showLabel && `${translate("Cart")} `}
                            <Typography
                                as="span"
                                {...styles.authorizedCartCount}
                                className="desktop-cart-count"
                                data-test-selector="cartLinkQuantity"
                            >
                                {retainedTotalCountToDisplay}
                            </Typography>
                        </Hidden>
                        <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
                    </StyledWrapper>
                </Link>
            ) : (
                <StyledWrapper {...styles.cartWrapper} className={`cartWrapper ${!isAuthenticated ? "not-authenticated-cart" : ""}`}>
                    {orderTotal && orderTotal > 0 ? (
                        <Typography as="span" {...styles.desktopCartPrice} data-test-selector="cartLinkTotal">
                            {orderTotalDisplay}
                        </Typography>
                    ) : null}
                    <Hidden as="span" className="desktop-cart-count-wrapper" {...styles.cartCountWrapper}>
                        <Icon src={ShoppingCart} size={18} />
                        {showLabel && `${translate("Cart")} `}
                        <Typography
                            as="span"
                            {...styles.desktopCartCount}
                            className="desktop-cart-count"
                            data-test-selector="cartLinkQuantity"
                        >
                            {retainedTotalCountToDisplay}
                        </Typography>
                    </Hidden>
                    <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
                </StyledWrapper>
            )}

        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
